/**
 * What the signup routes show, as data.
 *
 * Onboarding used to be decided in four places — a pair of nested ternaries
 * choosing the experience, two inline conditions choosing the chrome, and
 * separate branches in `MainApp` for the signed-out tree — reading nine
 * different inputs between them. Nothing could be checked without rendering
 * the whole app, so the combinations nobody demoed were the ones that broke:
 * a signed-in user on the control arm got the legacy quiz clipped at the
 * viewport, and `/onboarding` was missed when the sign-in modal's exemption
 * list was written.
 *
 * Everything that decides what a signup URL renders now lives here, as a pure
 * function over an explicit input. The routes render the answer and decide
 * nothing.
 *
 * ── The surfaces ──────────────────────────────────────────────────────────
 *
 *   /onboarding   the only signup surface. Every entry point leads here:
 *                 ad and landing CTAs (`onboardingHref`), the native start
 *                 page, `/` for signed-out visitors, `/quiz` for signed-out
 *                 visitors on legacy links, and `ProtectedRoute` for a signed
 *                 in user who never finished.
 *   /quiz         the answer editor, and nothing else. Reached from the
 *                 profile. Signing up here is over: the route forwards
 *                 signed-out visitors to `/onboarding`.
 *   /             signed-out visitors are redirected to `/onboarding` rather
 *                 than being served the flow inline, so the flow has exactly
 *                 one URL and one history behaviour.
 *
 * ── The experience ────────────────────────────────────────────────────────
 *
 * There is one: the narrative flow in components/onboarding. It beat the
 * paginated stepper in the `onboarding_flow` experiment (docs/experiments,
 * D-007) and the stepper is gone. The flow paints its own background, scrolls
 * itself and carries its own "Log in", so the route adds no chrome around it.
 */

export type OnboardingDecision =
  /** Auth or profile state isn't known yet. Deciding now would flash. */
  | { kind: "loading" }
  | { kind: "flow" }
  /** The answer editor. Always signed in. */
  | { kind: "editor" }
  | { kind: "redirect"; to: string };

export interface OnboardingRouteInput {
  /** Which of the three surfaces was requested. */
  route: "onboarding" | "quiz" | "root";
  /** `useAuth().isLoaded` — false while Firebase resolves the session. */
  isLoaded: boolean;
  isSignedIn: boolean;
}

export function resolveOnboardingRoute(
  input: OnboardingRouteInput,
): OnboardingDecision {
  const { route, isLoaded, isSignedIn } = input;

  if (!isLoaded) return { kind: "loading" };

  if (route === "root") {
    // Signed in, `/` is the dashboard and none of this applies.
    return isSignedIn
      ? { kind: "redirect", to: "/dashboard" }
      : { kind: "redirect", to: "/onboarding" };
  }

  if (route === "quiz") {
    // Legacy links and ad creative still point here.
    if (!isSignedIn) return { kind: "redirect", to: "/onboarding" };
    return { kind: "editor" };
  }

  // /onboarding always renders the flow, whoever asks for it.
  //
  // It briefly turned away anyone whose profile was already complete, on the
  // theory that a finished user has no business in signup. That was wrong on
  // both counts: it made the URL unusable for someone who wanted another look,
  // and it was unnecessary — `resolveResumeIndex` already opens a completed
  // profile at the screen after signup rather than at the intro, because every
  // question behind it is answered. The route needn't decide anything the flow
  // can work out from the answers.
  //
  // It also means this resolver never waits on the profile request, so the
  // flow is never held back by a fetch.
  return { kind: "flow" };
}

/**
 * Holds the flow on screen once it has started.
 *
 * `resolveOnboardingRoute` answers "should this user start onboarding" — a
 * question with one answer per visit. The route re-asks it on every render,
 * and the inputs it reads are the ones the flow itself changes as you walk it:
 * signing in flips `isSignedIn`, and the autosave then completes the profile
 * and stamps `quizCompletedAt`. Re-running entry rules on those threw the user
 * out to the dashboard from the middle of the flow they were finishing.
 *
 * Every bug in this area has had that shape — two things reacting to the same
 * async state and disagreeing about who owns the move. This is the transition
 * rule, kept pure so it can be tested as a sequence rather than as a snapshot,
 * because the snapshots were never what was broken.
 *
 * The flow navigates away itself when it finishes, so nothing else has to.
 */
export function settleOnboardingDecision(
  started: Extract<OnboardingDecision, { kind: "flow" }> | null,
  next: OnboardingDecision,
  route: OnboardingRouteInput["route"],
): OnboardingDecision {
  if (route !== "onboarding") return next;
  if (next.kind === "flow") return next;
  return started ?? next;
}
